import { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import { jwtDecode } from "jwt-decode";
import { closeChat, getChatHistory, addConversationTitle, getConversationTitle } from "./Chathistoryapi";

import { WEBCHAT_CONFIG, CHAT_API_BASE as CHAT_API, CHAT_AUTH_TOKEN } from "./webchatConfig";
import { customerAPI } from "../customerAPI";
import { authAPI } from "../authAPI";

/**
 * Format text as HTML lists
 */
function formatAsList(text) {
  const lines = text.split("\n");
  const listItems = [];
  let isList = false;
  let currentListType = "";

  lines.forEach((line) => {
    const bulletMatch = line.match(/^\s*[-*•]\s+(.*)/);
    const numberedMatch = line.match(/^\s*\d+(\.|\))\s+(.*)/);

    if (bulletMatch) {
      if (!isList || currentListType !== "ul") {
        if (isList) listItems.push(`</${currentListType}>`);
        listItems.push(`<ul>`);
        currentListType = "ul";
        isList = true;
      }
      listItems.push(`<li>${bulletMatch[1]}</li>`);
    } else if (numberedMatch) {
      if (!isList || currentListType !== "ol") {
        if (isList) listItems.push(`</${currentListType}>`);
        listItems.push(`<ol>`);
        currentListType = "ol";
        isList = true;
      }
      listItems.push(`<li>${numberedMatch[2]}</li>`);
    } else if (line.trim() === "" && isList) {
      listItems.push(`</${currentListType}>`);
      isList = false;
      currentListType = "";
    } else {
      if (isList) {
        listItems.push(`</${currentListType}>`);
        isList = false;
        currentListType = "";
      }
      if (line.trim()) {
        listItems.push(`<p>${line.trim()}</p>`);
      }
    }
  });

  if (isList) {
    listItems.push(`</${currentListType}>`);
  }

  return listItems.join("");
}

/**
 * Format text with markdown-like syntax
 */
function formatText(text) {
  if (!text) return "";

  text = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  text = text.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
  text = text.replace(/\*(.*?)\*/g, "<em>$1</em>");
  text = text.replace(
    /\[(.*?)\]\((https?:\/\/[^\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  text = text.replace(
    /\[(.*?)\]\(mailto:(.*?)\)/g,
    '<a href="mailto:$2">$1</a>'
  );
  text = text.replace(/\[(.*?)\]\(tel:(.*?)\)/g, '<a href="tel:$2">$1</a>');
  return formatAsList(text);
}

/**
 * Save chat to history in localStorage
 */
const saveChatToHistory = (conversationId, userId, firstMessage) => {
  try {
    const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
    const storedHistory = localStorage.getItem(historyKey);
    let chatHistory = storedHistory ? JSON.parse(storedHistory) : [];

    const existingIndex = chatHistory.findIndex(
      (chat) => chat.conversationKey === conversationId
    );

    const chatEntry = {
      id: conversationId,
      conversationKey: conversationId,
      title: firstMessage.length > 50 ? firstMessage.substring(0, 50) + '…' : firstMessage || "New Conversation",
      lastMessage: firstMessage,
      timestamp: new Date().toISOString(),
      userId: userId,
      messageCount: 1,
    };

    if (existingIndex !== -1) {
      chatHistory[existingIndex] = {
        ...chatHistory[existingIndex],
        lastMessage: firstMessage,
        timestamp: new Date().toISOString(),
        messageCount: (chatHistory[existingIndex].messageCount || 0) + 1,
      };
    } else {
      chatHistory.unshift(chatEntry);
    }

    chatHistory = chatHistory.slice(0, 50);

    localStorage.setItem(historyKey, JSON.stringify(chatHistory));
    console.log("💾 Saved chat to history:", chatEntry);
  } catch (error) {
    console.error("❌ Error saving chat to history:", error);
  }
};

/**
 * Re-key an existing chat history entry when the backend swaps the
 * conversation id mid-conversation (e.g. CONVO_ placeholder -> real conv_ id).
 * Without this, the old entry is orphaned and the next message gets logged
 * as a brand-new conversation in the sidebar.
 */
const renameChatHistoryKey = (oldId, newId) => {
  try {
    if (!oldId || !newId || oldId === newId) return;

    const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
    const storedHistory = localStorage.getItem(historyKey);
    if (!storedHistory) return;

    let chatHistory = JSON.parse(storedHistory);
    const idx = chatHistory.findIndex((chat) => chat.conversationKey === oldId);

    if (idx !== -1) {
      chatHistory[idx] = {
        ...chatHistory[idx],
        id: newId,
        conversationKey: newId,
      };
      localStorage.setItem(historyKey, JSON.stringify(chatHistory));
      console.log("🔁 Re-keyed chat history entry:", oldId, "→", newId);
    } else {
      console.warn("⚠️ renameChatHistoryKey: no existing entry found for", oldId);
    }
  } catch (error) {
    console.error("❌ Error renaming chat history key:", error);
  }
};

/**
 * Update chat message count in history
 */
const updateChatHistory = (conversationId, lastMessage) => {
  try {
    const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
    const storedHistory = localStorage.getItem(historyKey);
    if (!storedHistory) return;

    let chatHistory = JSON.parse(storedHistory);
    const chatIndex = chatHistory.findIndex(
      (chat) => chat.conversationKey === conversationId
    );

    if (chatIndex !== -1) {
      chatHistory[chatIndex] = {
        ...chatHistory[chatIndex],
        lastMessage: lastMessage,
        timestamp: new Date().toISOString(),
        messageCount: (chatHistory[chatIndex].messageCount || 0) + 1,
      };

      localStorage.setItem(historyKey, JSON.stringify(chatHistory));
      console.log("💾 Updated chat history for:", conversationId);
    }
  } catch (error) {
    console.error("❌ Error updating chat history:", error);
  }
};

/**
 * Update chat history title
 */
export const updateChatHistoryTitle = (conversationId, newTitle) => {
  try {
    const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
    const storedHistory = localStorage.getItem(historyKey);
    if (!storedHistory) return;

    let chatHistory = JSON.parse(storedHistory);
    const chatIndex = chatHistory.findIndex(
      (chat) => chat.conversationKey === conversationId
    );

    if (chatIndex !== -1) {
      chatHistory[chatIndex] = {
        ...chatHistory[chatIndex],
        title: newTitle,
      };

      localStorage.setItem(historyKey, JSON.stringify(chatHistory));
      console.log("💾 Updated chat title for:", conversationId, "to", newTitle);
    }
  } catch (error) {
    console.error("❌ Error updating chat history title:", error);
  }
};

export const useWebChat = (user) => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const [planExpired, setPlanExpired] = useState(false);
  const [notification, setNotification] = useState(null);
  const [responseDelayed, setResponseDelayed] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const notifyError = useCallback((message) => {
    setNotification({ type: 'error', message, id: Date.now() });
  }, []);

  const dismissResponseDelayed = useCallback(() => {
    setResponseDelayed(false);
  }, []);

  useEffect(() => {
    console.log("UseWebChat State: isTyping =", isTyping);
  }, [isTyping]);

  const [chatStatus, setChatStatus] = useState("Bot");
  const [isConnected, setIsConnected] = useState(false);

  const socketRef = useRef(null);
  const firstMessageSentRef = useRef(false);
  const userSessionRef = useRef(null);

  // True after resetChat() clears conversation ids. Prevents delayed socket
  // messages from an old conversation from re-attaching the session.
  const sessionResetRef = useRef(false);
  const sessionResetVersionRef = useRef(0);
  // IDs of conversations closed by resetChat — used to explicitly reject their
  // delayed socket messages even after sessionResetRef is cleared.
  const closedConversationIdsRef = useRef(new Set());

  // Track whether the UI is waiting for the final assistant response. Botpress
  // lifecycle events like "typing stopped" must not clear the thinking state
  // while an n8n async response is still pending.
  const awaitingBotResponseRef = useRef(false);
  const awaitingConversationRef = useRef("");

  const onPostPreviewRef = useRef(null);

  // ── Track which message IDs have already been dispatched as post previews
  // to avoid duplicates between socket delivery and polling.
  const dispatchedPostPreviewIds = useRef(new Set());
  const pendingExecutionIdRef = useRef(null);
  const cancelOnNextExecIdRef = useRef(false);
  const [canStop, setCanStop] = useState(false);

  const [userSession, setUserSession] = useState(() => {
    const stored = localStorage.getItem(WEBCHAT_CONFIG.clientName);
    const initialSession = stored
      ? JSON.parse(stored)
      : {
        userToken: "",
        conversationId: "",
        userId: "",
        webchatId: "",
        vcxConversationId: "",
        internalConversationId: "",
      };
    userSessionRef.current = initialSession;
    return initialSession;
  });

  useEffect(() => {
    userSessionRef.current = userSession;
    console.log("📝 Session synced:", userSession);
  }, [userSession]);

  const userTokenRef = useRef(null);
  const deductionTimeoutRef = useRef(null);
  const responseTimeoutRef = useRef(null);
  const sessionInitPromiseRef = useRef(null);
  const pendingBlurRef = useRef(null);

  useEffect(() => {
    const token =
      user?.token ||
      user?.jwt ||
      user?.accessToken ||
      user?.data?.token ||
      user?.data?.jwt ||
      user?.data?.accessToken ||
      userSession?.userToken;

    if (user) {
      console.log("👤 User object keys:", Object.keys(user));
      if (user.data) console.log("👤 User.data keys:", Object.keys(user.data));
    }

    console.log("🔑 Selected Token Source:", token ? (token === userSession?.userToken ? "Session Token" : "User Auth Token") : "None");
    userTokenRef.current = token;
  }, [user, userSession]);

  const hasAssignedUserRef = useRef(false);

  useEffect(() => {
    const performUserAssignment = async () => {
      if (user?.isNewSignup && !hasAssignedUserRef.current) {
        let companyCustomerPriEmail = user.companyCustomerPriEmail || user.email;
        let companyCustomerId = user.companyCustomerId || user.id;

        const token = userTokenRef.current;
        const chatUserId = userSession.userId;
        const chatUserToken = userSession.userToken;

        if ((!companyCustomerPriEmail || !companyCustomerId) && token) {
          try {
            const decoded = jwtDecode(token);
            if (!companyCustomerPriEmail) companyCustomerPriEmail = decoded.email || decoded.companyCustomerPriEmail;
            if (!companyCustomerId) companyCustomerId = decoded.customerId || decoded.companyCustomerId;
          } catch (e) {
            console.error("⚠️ Failed to decode token for assignment:", e);
          }
        }

        if (companyCustomerPriEmail && companyCustomerId && chatUserId && chatUserToken) {
          console.log("🔗 Triggering Assign User (Signup)...", {
            companyCustomerPriEmail,
            companyCustomerId,
            user_id: chatUserId
          });

          hasAssignedUserRef.current = true;

          const result = await customerAPI.assignUser(
            companyCustomerPriEmail,
            companyCustomerId,
            chatUserId,
            chatUserToken
          );

          if (result.success) {
            console.log("✅ User assigned successfully:", result.data);

            console.log("🔄 Assigning user role...");
            const roleResult = await authAPI.addUserRole(
              "62b58b1d-4698-4d92-b29a-fe113c7b0310",
              chatUserId,
              "market_researcher",
              CHAT_AUTH_TOKEN
            );

            if (roleResult.success) {
              console.log("✅ User role assigned successfully:", roleResult.data);

              try {
                const fullName = user?.name || "User";
                console.log("🔄 Updating fullname in webchat:", fullName);
                const updateNameRes = await fetch(
                  `https://chat.versalence.info/webchat/v2/user/update-fullname/${chatUserId}/62b58b1d-4698-4d92-b29a-fe113c7b0310/${encodeURIComponent(fullName)}`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                  }
                );

                if (updateNameRes.ok) {
                  console.log("✅ Fullname updated successfully in webchat");
                } else {
                  console.error("❌ Failed to update fullname. Status:", updateNameRes.status);
                }
              } catch (updateErr) {
                console.error("❌ Error updating fullname in webchat:", updateErr);
              }

            } else {
              console.error("❌ Failed to assign user role:", roleResult.error);
            }
          } else {
            console.error("❌ Failed to assign user:", result.error);
          }
        } else {
          console.warn("⚠️ Skipping assign-user: Missing required data", {
            hasEmail: !!companyCustomerPriEmail,
            hasCid: !!companyCustomerId,
            hasChatId: !!chatUserId,
            hasChatToken: !!chatUserToken
          });
        }
      }
    };

    performUserAssignment();
  }, [user?.isNewSignup, user?.companyCustomerPriEmail, user?.companyCustomerId, userSession.userId, userSession.userToken]);

  useEffect(() => {
    const checkCreditsOnLoad = async () => {
      const token = userTokenRef.current;
      const userId = userSession?.userId;

      if (!token || !userId) {
        console.log("⏭️ Skipping initial credit check: No token or userId yet");
        return;
      }

      try {
        console.log("💰 Checking credits on page load for user:", userId);
        const res = await customerAPI.getCredits(token, userId);

        if (res.success && res.data) {
          const creditsData = res.data.data || res.data;
          const creditsRemaining = creditsData.creditsRemaining;

          console.log("✅ Credits on load:", creditsRemaining);

          if (creditsRemaining !== undefined && creditsRemaining <= 0) {
            console.warn("⚠️ Insufficient credits on page load! Blocking chat.");
            setInsufficientCredits(true);
          } else {
            setInsufficientCredits(false);
          }

          if (creditsData.extraCharRequired > 0 && (creditsRemaining === undefined || creditsRemaining <= 0)) {
            console.log("⚠️ Limit reached on load (Extra Char Req: " + creditsData.extraCharRequired + "). Storing for message load.");

            pendingBlurRef.current = {
              required: creditsData.extraCharRequired,
              total: creditsData.lastOutputChars || 0
            };

            setMessages(prev => {
              if (prev.length === 0) return prev;

              const updated = [...prev];
              let applied = false;
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].type === "assistant" && updated[i].messageType !== "choice") {
                  updated[i] = {
                    ...updated[i],
                    blurInfo: pendingBlurRef.current
                  };
                  applied = true;
                  break;
                }
              }
              return applied ? updated : prev;
            });
          }
        }
      } catch (error) {
        console.error("❌ Error checking credits on load:", error);
      }
    };

    checkCreditsOnLoad();
  }, [userSession?.userId, userTokenRef.current]);

  const externalId = useRef("");
  useEffect(() => {
    try {
      const externalIdValue = localStorage.getItem("external_id");
      if (externalIdValue && externalIdValue !== "not-provided") {
        externalId.current = externalIdValue;
      }
    } catch (error) {
      console.error("Error reading external_id:", error);
    }
  }, []);

  const initializingRef = useRef(false);

  useEffect(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    const websocketUrl = CHAT_API.replace("/webchat/v2", "");
    socketRef.current = io(websocketUrl);

    console.log("User id on socket load", userSession.userId);

    socketRef.current.on("connect", () => {
      console.log("✅ WebChat connected");
      setIsConnected(true);
      console.log("User Id from Socket ", userSession.userId);
      if (userSession.userId) {
        socketRef.current.emit("subscribe", userSession.userId);
      }
    });

    socketRef.current.onAny((event, ...args) => {
      console.log(`Received event: ${event}`, args);
    });

    socketRef.current.on("disconnect", () => {
      console.log("❌ WebChat disconnected");
      setIsConnected(false);
    });

    socketRef.current.on("typing started", () => {
      setIsTyping(true);
    });

    socketRef.current.on("typing stopped", () => {
      if (awaitingBotResponseRef.current) {
        console.log("⏭️ Ignoring Botpress typing stopped while n8n response is pending");
        return;
      }
      setIsTyping(false);
    });

    socketRef.current.on("sending message", (msg) => {
      console.log("UseWebChat: Received message via socket");
      handleIncomingMessage(msg);
    });

    socketRef.current.on("toggle update", (data) => {
      setChatStatus(data === "Human" ? "Human" : "Bot");
      if (data === "Human") {
        awaitingBotResponseRef.current = false;
        setIsTyping(false);
      }
    });

    const initSession = async () => {
      let currentUserId = userSession.userId;

      if (!currentUserId && userTokenRef.current) {
        try {
          const decoded = jwtDecode(userTokenRef.current);
          if (decoded.userId) {
            console.log("🔄 Recovered userId from token:", decoded.userId);
            currentUserId = decoded.userId;
            const recoveredSession = {
              ...userSession,
              userId: decoded.userId,
            };
            setUserSession(recoveredSession);
            userSessionRef.current = recoveredSession;
          }
        } catch (e) {
          console.error("Failed to decode token for userId:", e);
        }
      }

      const isSignup = user?.isNewSignup;
      const hasValidSession = !!(currentUserId && currentUserId !== "");

      console.log("🚦 Init Flow Mode:", (isSignup && !hasValidSession) ? "SIGNUP (New Session)" : "LOGIN/RELOAD (Fetch History)");

      if (isSignup && !hasValidSession) {
        await initializeNewSession();
      } else {
        if (currentUserId) {
          const success = await loadPreviousMessages();
          if (success) {
            console.log("✅ Login/Reload: History loaded, session active.");
          } else {
            console.log("⚠️ Login/Reload: No history found. Session will auto-create on first message.");
          }
        } else {
          console.warn("⚠️ Login/Reload: No userId found in session or token. Chat state is empty.");
        }
      }
    };

    initSession();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      initializingRef.current = false;
    };
  }, [user]);

  useEffect(() => {
    localStorage.setItem(
      WEBCHAT_CONFIG.clientName,
      JSON.stringify(userSession)
    );
  }, [userSession]);

  const isUserSideMessage = (msg) => {
    if (msg?.messageFrom) {
      const mf = msg.messageFrom.toLowerCase();
      if (mf === "user" || mf === "customer") return true;
      if (mf === "bot" || mf === "assistant" || mf === "agent") return false;
    }

    const from = msg?.content?.data?.from;
    if (typeof from === "string") {
      const f = from.toLowerCase();
      if (f === "user" || f === "customer") return true;
      if (f === "bot" || f === "assistant" || f === "agent") return false;
    }

    const senderId = msg?.senderId || msg?.sender_id || msg?.sender;
    if (senderId && typeof senderId === "string") {
      if (senderId.startsWith("user_")) return true;
      if (senderId.startsWith("bot_") || senderId === WEBCHAT_CONFIG.botId) return false;
    }

    const recipientId = msg?.recipientId || msg?.recipient_id || msg?.recipient;
    if (recipientId && typeof recipientId === "string") {
      if (recipientId.startsWith("user_")) return false;
      if (recipientId.startsWith("bot_") || recipientId === WEBCHAT_CONFIG.botId) return true;
    }

    if (msg?.direction) {
      const dir = msg.direction.toLowerCase();
      if (dir === "outgoing" || dir === "out") return true;
      if (dir === "incoming" || dir === "in") return false;
    }

    console.warn("⚠️ Could not determine message origin, defaulting to bot:", msg);
    return false;
  };

  const processMessage = (msg) => {
    const isUser = isUserSideMessage(msg);
    const newMessage = {
      id: msg._id || Date.now(),
      type: isUser ? "user" : "assistant",
      content: "",
      timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
      payload: msg.content,
      messageType: msg.content?.type,
      questionFor: msg.content?.data?.questionFor,
      senderId: msg.senderId || msg.sender_id,
      chatId: msg.chatId,
    };

    if (msg.content?.type === "text") {
      const rawText = msg.content?.data?.text || msg.content?.data?.body || "";
      newMessage.content = rawText;
      newMessage.isHtml = false;
    } else if (msg.content?.type === "image") {
      newMessage.content = "Image";
      newMessage.imageUrl = msg.content?.data?.imageUrl;
    } else if (msg.content?.type === "choice") {
      newMessage.content = msg.content?.data?.text || "Choose an option:";
      newMessage.quickReplies = msg.content?.data?.options?.map((opt) => ({
        title: opt.label,
        payload: opt.value,
      }));
    } else if (msg.content?.type === "carousel") {
      newMessage.content = "Carousel";
      newMessage.cards = msg.content?.data?.items?.map((item) => ({
        title: item.title,
        subtitle: item.subtitle,
        image: item.imageUrl,
        description: item.description,
        buttons: item.actions?.map((action) => ({
          title: action.label,
          url: action.action === "url" ? action.value : null,
          payload: action.action !== "url" ? action.value : null,
        })),
      }));
    } else if (msg.content?.type === "card") {
      newMessage.content = "Card";
      newMessage.cards = [
        {
          title: msg.content?.data?.title,
          subtitle: msg.content?.data?.subtitle,
          image: msg.content?.data?.imageUrl,
          buttons: msg.content?.data?.actions?.map((action) => ({
            title: action.label,
            url: action.action === "url" ? action.value : null,
            payload: action.action !== "url" ? action.value : null,
          })),
        },
      ];
    } else if (msg.content?.type === "form") {
      newMessage.content = "Form";
      newMessage.formFields = msg.content?.data?.formfields;
      newMessage.formDescription = msg.content?.data?.description;
      newMessage.formId = msg._id;
    } else if (msg.content?.type === "video") {
      newMessage.content = "Video";
      newMessage.videoUrl = msg.content?.data?.videoUrl;
    } else if (msg.content?.type === "audio") {
      newMessage.content = "Audio";
      newMessage.audioUrl = msg.content?.data?.audioUrl;
    } else if (msg.content?.type === "location") {
      newMessage.content = "Location";
      newMessage.location = {
        latitude: msg.content?.data?.latitude,
        longitude: msg.content?.data?.longitude,
        address: msg.content?.data?.address,
        title: msg.content?.data?.title,
      };
    } else {
      newMessage.content = msg.content?.data?.text || msg.content?.data?.body || "Media Content";
    }

    return newMessage;
  };

  // ── HELPER: try to dispatch post preview from a raw text string ──────────
  // Returns true if the text contained a post preview and it was dispatched.
  const tryDispatchPostPreview = useCallback((text, messageId) => {
    if (!text) return false;
    if (
      !text.includes('__POST_PREVIEW__') ||
      !text.includes('__END_POST_PREVIEW__')
    ) return false;

    // Deduplicate by message ID (socket + poll may both fire)
    if (messageId && dispatchedPostPreviewIds.current.has(messageId)) {
      console.log('🔁 Post preview already dispatched for ID:', messageId);
      return true; // Return true so callers know it's a preview message
    }

    try {
      const jsonStr = text
        .split('__POST_PREVIEW__')[1]
        .split('__END_POST_PREVIEW__')[0]
        .trim();
      const posts = JSON.parse(jsonStr);
      if (Array.isArray(posts) && posts.length > 0 && onPostPreviewRef.current) {
        console.log('📬 Post preview dispatched, source:', messageId ? 'socket' : 'poll');
        if (messageId) dispatchedPostPreviewIds.current.add(messageId);
        onPostPreviewRef.current(posts);
        return true;
      }
    } catch (e) {
      console.error('❌ Failed to parse post preview:', e);
    }
    return false;
  }, []);

  // Load previous messages from API
  const loadPreviousMessages = async () => {
    const targetUserId = userSessionRef.current?.userId || userSession.userId;
    if (!targetUserId) return false;
    const loadResetVersion = sessionResetVersionRef.current;
    const isStaleHistoryLoad = () =>
      loadResetVersion !== sessionResetVersionRef.current || sessionResetRef.current;

    try {
      console.log("📥 Fetching previous messages for user:", targetUserId);

      // STEP 1: Get conversation history
      const historyRes = await fetch(
        `${CHAT_API}/conversation/get-conversation-history/${targetUserId}/${WEBCHAT_CONFIG.clientId}`
      );
      if (isStaleHistoryLoad()) {
        console.log("Skipping stale conversation history load after reset.");
        return false;
      }

      // ── conversationId from history API (can be a stale CONVO_ id) ──
      let historyConversationId = null;
      // ── vcxConversationId from history API = the live Botpress CONVO_ id ──
      let activeVcxConversationId = null;

      if (historyRes.ok) {
        const historyData = await historyRes.json();
        if (isStaleHistoryLoad()) {
          console.log("Skipping stale conversation history response after reset.");
          return false;
        }

        if (historyData?.data && Array.isArray(historyData.data)) {
          const activeConversation = historyData.data.find(
            (conv) => conv.status === "active"
          );
          if (activeConversation) {
            // conversationId from history — may be stale, do NOT use for message fetch
            historyConversationId = activeConversation.conversationId;

            // vcxConversationId from history = the live Botpress CONVO_ id
            activeVcxConversationId =
              activeConversation.vcxConversationId ||
              activeConversation.vcx_conversation_id ||
              activeConversation.vcx_id || "";

            console.log("📋 Active conversation IDs:", {
              history_id: historyConversationId,     // may be stale CONVO_
              vcx_id: activeVcxConversationId,       // live Botpress CONVO_
              allKeys: Object.keys(activeConversation)
            });

            // ── Patch session immediately with CONVO_ ID ──────────────────
            if (activeVcxConversationId) {
              if (isStaleHistoryLoad()) {
                console.log("Skipping stale active conversation patch after reset.");
                return false;
              }
              const patched = {
                ...userSessionRef.current,
                // Store the full CONVO_ ID as conversationId so n8n/Dashboard
                // always receives the correct ID
                conversationId: activeVcxConversationId,
                vcxConversationId: activeVcxConversationId,
                // internalConversationId must be a conv_ ID, not a CONVO_ ID.
                // The history API does not expose it, so keep whatever we already have.
                internalConversationId: userSessionRef.current.internalConversationId,
              };
              setUserSession(patched);
              userSessionRef.current = patched;
              sessionResetRef.current = false;
              console.log("✅ Session patched with CONVO_ ID:", activeVcxConversationId);
            }
          }
        }
      }

      if (!activeVcxConversationId) {
        console.log("⚠️ No active vcxConversationId found.");
        return false;
      }

      // STEP 3: Fetch messages — must use the live vcxConversationId (CONVO_...)
      const response = await fetch(
        `${CHAT_API}/chat/get-botpress-messages-infinium/${targetUserId}/${activeVcxConversationId}/${WEBCHAT_CONFIG.clientId}`
      );
      if (isStaleHistoryLoad()) {
        console.log("Skipping stale message history load after reset.");
        return false;
      }

      const uuid = WEBCHAT_CONFIG.uuid;
      const currentConvId = userSessionRef.current?.conversationId;

      if (currentConvId) {
        getConversationTitle(currentConvId, uuid).then(res => {
          const GENERIC_TITLES = ["Your Conversation", "Conversation", "New Conversation", ""];
          const name = res.data?.conversationName?.trim();
          if (res.success && name && !GENERIC_TITLES.includes(name)) {
            console.log("📝 Updating local history with title from API:", name);
            updateChatHistoryTitle(currentConvId, name);
          }
        }).catch(err => console.error("Failed to fetch title on load:", err));
      }

      if (response.ok) {
        const data = await response.json();
        if (isStaleHistoryLoad()) {
          console.log("Skipping stale message history response after reset.");
          return false;
        }
        console.log("📦 get-botpress-messages-infinium raw response:", {
          keys: Object.keys(data),
          messageCount: Array.isArray(data.message) ? data.message.length : 0,
          messagesCount: Array.isArray(data.messages) ? data.messages.length : 0,
          dataCount: Array.isArray(data.data) ? data.data.length : 0,
          sample: Array.isArray(data.message) && data.message.length > 0
            ? data.message.slice(0, 2).map(m => ({
                id: m._id,
                type: m.content?.type,
                from: m.messageFrom || m.content?.data?.from,
                senderId: m.senderId,
                text: (m.content?.data?.text || m.content?.data?.body || "").substring(0, 40),
                vcx: m.vcxConversationId || m.vcx_conversation_id,
                wc: m.wcconversationId,
              }))
            : null,
        });

        // ── Only sync userToken and userId from messages response.
        // ── Do NOT overwrite conversationId here — we already set the CONVO_ ID above.
        let changes = false;
        let nextSession = { ...userSessionRef.current };

        const foundUserToken = data.userToken || data.user_token || "";
        const foundUserId = data.userId || data.user_id || "";

        const rawMessages = data.message || data.messages || data.data || [];

        // Also scan messages array for ids if we didn't get them from history endpoint
        if (Array.isArray(rawMessages)) {
          const msgWithVcx = rawMessages.find(m => m.vcxConversationId || m.vcx_conversation_id);
          if (msgWithVcx && !nextSession.vcxConversationId) {
            const vcxId = msgWithVcx.vcxConversationId || msgWithVcx.vcx_conversation_id || "";
            if (vcxId) {
              console.log("✅ Found vcxConversationId in message array:", vcxId);
              nextSession.conversationId = vcxId;
              nextSession.vcxConversationId = vcxId;
              changes = true;
            }
          }

          const msgWithWc = rawMessages.find(m => m.wcconversationId || m.wc_conversation_id);
          if (msgWithWc && !nextSession.internalConversationId) {
            const wcId = msgWithWc.wcconversationId || msgWithWc.wc_conversation_id || "";
            if (wcId.startsWith("conv_")) {
              console.log("✅ Found internal conv_ ID in message array:", wcId);
              nextSession.internalConversationId = wcId;
              changes = true;
            }
          }
        }

        if (foundUserToken && foundUserToken !== userSessionRef.current.userToken) {
          console.log("✅ syncing userToken from history");
          nextSession.userToken = foundUserToken;
          changes = true;
        }

        if (foundUserId && foundUserId !== userSessionRef.current.userId) {
          console.log("✅ syncing userId from history:", foundUserId);
          nextSession.userId = foundUserId;
          changes = true;
        }

        if (changes) {
          if (isStaleHistoryLoad()) {
            console.log("Skipping stale session sync from history after reset.");
            return false;
          }
          setUserSession(nextSession);
          userSessionRef.current = nextSession;
        }

        // STEP 4: Fetch user-scoped messages to catch bot responses saved with
        // conversationId: null (backend bug). Filter to the current conversation
        // using the wcconversationId we just discovered.
        let userScopedMessages = [];
        try {
          const userScopedRes = await fetch(
            `${CHAT_API}/chat/get-botpress-messages/${targetUserId}/${WEBCHAT_CONFIG.clientId}`
          );
          if (userScopedRes.ok) {
            const userScopedData = await userScopedRes.json();
            const allUserScoped = userScopedData.message || userScopedData.messages || userScopedData.data || [];
            const wcConvId = nextSession.internalConversationId || userSessionRef.current.internalConversationId;
            const vcxConvId = nextSession.vcxConversationId || userSessionRef.current.vcxConversationId;

            userScopedMessages = allUserScoped.filter((msg) => {
              const msgWc = msg.wcconversationId || msg.wc_conversation_id || "";
              const msgConv = msg.conversationId || msg.conversation_id || msg.vcxConversationId || msg.vcx_conversation_id || "";
              const matchesWc = wcConvId && msgWc && msgWc === wcConvId;
              const matchesVcx = vcxConvId && msgConv && msgConv === vcxConvId;
              return matchesWc || matchesVcx;
            });

            console.log("📦 get-botpress-messages user-scoped fallback:", {
              allCount: allUserScoped.length,
              filteredCount: userScopedMessages.length,
              wcConvId,
              vcxConvId,
            });
          }
        } catch (e) {
          console.warn("⚠️ User-scoped message fetch failed:", e);
        }

        // Merge conversation-scoped and user-scoped messages by id.
        const mergedRawMap = new Map();
        [...rawMessages, ...userScopedMessages].forEach((msg) => {
          if (msg && msg._id) mergedRawMap.set(msg._id, msg);
        });
        const mergedRawMessages = Array.from(mergedRawMap.values());

        if (mergedRawMessages.length > 0) {
          if (isStaleHistoryLoad()) {
            console.log("Skipping stale message render after reset.");
            return false;
          }
          const formattedMessages = mergedRawMessages
            .filter((msg) => {
              if (msg.content?.type === "text") {
                const text =
                  msg.content?.data?.text || msg.content?.data?.body || "";
                if (text.startsWith('__EXEC_ID__:')) return false;
                return text.trim().length > 0;
              }
              return true;
            })
            .map((msg) => processMessage(msg))
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

          if (pendingBlurRef.current) {
            console.log("Applying pending blur to loaded messages");
            for (let i = formattedMessages.length - 1; i >= 0; i--) {
              if (formattedMessages[i].type === "assistant" && formattedMessages[i].messageType !== "choice") {
                formattedMessages[i].blurInfo = pendingBlurRef.current;
                break;
              }
            }
          }

          setMessages(formattedMessages);
          console.log("✅ Loaded previous messages:", formattedMessages.length);

          return true;
        }
        return true;
      } else {
        console.warn(
          "⚠️ Failed to load previous messages, status:",
          response.status
        );
        return false;
      }
    } catch (error) {
      console.error("❌ Error loading previous messages:", error);
      notifyError("Failed to load previous messages");
      return false;
    }
  };

  // Initialize new session via webhook
  // skipGreeting: when true (e.g. lazy init from sendMessage), do not inject a
  // greeting into the message list because the user's own message is about to be added.
  const initializeNewSession = async (skipGreeting = false) => {
    if (sessionInitPromiseRef.current) {
      console.log("🚀 Session initialization already in progress, waiting...");
      return sessionInitPromiseRef.current;
    }

    console.log("🚀 Initializing new session via webhook...");

    sessionInitPromiseRef.current = (async () => {
      try {
        const currentSession = userSessionRef.current;

        const payload = {
          conversation: {
            userId: currentSession.userId || "",
            conversation_id: currentSession.conversationId || "",
            userToken: currentSession.userToken || "",
          },
          message: "",
          toggle_status: "Bot",
          clientId: WEBCHAT_CONFIG.uuid,
          webhookId: WEBCHAT_CONFIG.webhookId,
          vcxConversationId: currentSession.vcxConversationId || "",
        };

        if (externalId.current) {
          payload.external_id = externalId.current;
        }

        console.log("📤 Sending init payload:", payload);

        const response = await fetch(
          `${CHAT_API}/wc-webhook/recieve-webchat-message/${WEBCHAT_CONFIG.uuid}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log("✅ Init session success:", result);

          const resRoot = result.data?.res || result.data || result;

          const newUserId = result.userId || result.data?.userId;
          const newVcxId = result.data?.vcxConversationId;
          const newConvId = resRoot.conversation_id;
          const newUserToken = resRoot.user_token;

          if (newUserId && newConvId) {
            const newSession = {
              userId: newUserId,
              webchatId: newUserId,
              conversationId: newVcxId || newConvId,
              userToken: newUserToken || "",
              vcxConversationId: newVcxId || "",
              internalConversationId: newConvId,
            };

            setUserSession(newSession);
            userSessionRef.current = newSession;
            sessionResetRef.current = false;

            let initialMessageText = "";

            if (result.output && Array.isArray(result.output) && result.output.length > 0) {
              const firstOutput = result.output[0];
              if (firstOutput.content && Array.isArray(firstOutput.content) && firstOutput.content.length > 0) {
                initialMessageText = firstOutput.content[0].text || "";
              }
            } else if (result.content?.data?.body) {
              initialMessageText = result.content.data.body;
            }

            if (initialMessageText && initialMessageText.trim().length > 0 && !skipGreeting) {
              const initialMsg = {
                id: Date.now(),
                type: "assistant",
                content: initialMessageText,
                timestamp: new Date(),
              };
              setMessages([initialMsg]);

              if (newUserId && newConvId) {
                console.log("📝 Auto-setting conversation title:", initialMessageText);
                addConversationTitle(newConvId, WEBCHAT_CONFIG.uuid, initialMessageText)
                  .then(res => {
                    if (res.success) {
                      updateChatHistoryTitle(newConvId, initialMessageText);
                    }
                  })
                  .catch(err => console.error("❌ Failed to auto-set title:", err));
              }
            }

            if (socketRef.current) {
              socketRef.current.emit("subscribe", newUserId);
            }
          }
        } else {
          console.error("❌ Failed to initialize session:", response.status);
          notifyError(`Failed to initialize session (Status: ${response.status})`);
        }
      } catch (e) {
        console.error("❌ Error initializing session:", e);
        notifyError("Error initializing session: " + e.message);
      } finally {
        sessionInitPromiseRef.current = null;
      }
    })();

    return sessionInitPromiseRef.current;
  };

  const fetchAndAppendMissedMessages = async () => {
    const currentSession = userSessionRef.current;
    if (!currentSession.userId) return;

    // Only fetch missed messages for the current conversation. Without a
    // current conversation id we would pull in every message for the user.
    const currentInternalId = currentSession.internalConversationId;
    const currentVcxId = currentSession.vcxConversationId || currentSession.conversationId;
    if (!currentInternalId && !currentVcxId) {
      console.log("⚠️ fetchAndAppendMissedMessages: no current conversation id, skipping.");
      return;
    }

    try {
      await new Promise((r) => setTimeout(r, 400));

      const response = await fetch(
        `${CHAT_API}/chat/get-botpress-messages/${currentSession.userId}/${WEBCHAT_CONFIG.clientId}`
      );

      if (response.ok) {
        const data = await response.json();
        console.log("📦 get-botpress-messages raw response:", {
          keys: Object.keys(data),
          messageCount: Array.isArray(data.message) ? data.message.length : 0,
          messagesCount: Array.isArray(data.messages) ? data.messages.length : 0,
          dataCount: Array.isArray(data.data) ? data.data.length : 0,
        });

        const rawMessages = data.message || data.messages || data.data || [];
        if (Array.isArray(rawMessages) && rawMessages.length > 0) {
          console.log("🔍 fetchAndAppendMissedMessages: session ids:", {
            currentInternalId,
            currentVcxId,
            rawCount: rawMessages.length,
            rawSample: rawMessages.slice(0, 5).map(m => ({
              id: m._id,
              from: m.messageFrom || m.content?.data?.from,
              type: m.content?.type,
              wc: m.wcconversationId,
              conv: m.conversationId,
              vcx: m.vcxConversationId || m.vcx_conversation_id,
              ts: m.timestamp,
            })),
          });

          setMessages((prevMessages) => {
            const incomingMessages = rawMessages
              .filter((msg) => {
                // Keep only messages belonging to the current conversation
                const msgConvId = msg.conversationId || msg.vcxConversationId || msg.vcx_conversation_id || "";
                const msgWcId = msg.wcconversationId || "";
                const matchesCurrent =
                  (currentVcxId && msgConvId === currentVcxId) ||
                  (currentInternalId && (msgConvId === currentInternalId || msgWcId === currentInternalId));
                if (!matchesCurrent) return false;

                if (msg.content?.type === "text") {
                  const text =
                    msg.content?.data?.text || msg.content?.data?.body || "";
                  if (text.startsWith('__EXEC_ID__:')) return false;
                  return text.trim().length > 0;
                }
                return true;
              })
              .map((msg) => processMessage(msg));

            let updatedMessages = [...prevMessages];
            let changesMade = false;

            incomingMessages.forEach((newMsg) => {
              const existingIndex = updatedMessages.findIndex(
                (m) => m.id === newMsg.id
              );
              if (existingIndex !== -1) return;

              const optimisticIndex = updatedMessages.findIndex(
                (localMsg) =>
                  localMsg.type === "user" &&
                  newMsg.type === "user" &&
                  (localMsg.content === newMsg.content || formatText(localMsg.content) === newMsg.content) &&
                  (typeof localMsg.id === "number" ||
                    (typeof localMsg.id === "string" &&
                      !localMsg.id.includes("-") &&
                      localMsg.id.length < 20)) &&
                  typeof newMsg.id === "string"
              );

              if (optimisticIndex !== -1) {
                console.log(
                  "🔄 Merging optimistic message with real one:",
                  updatedMessages[optimisticIndex].id,
                  "->",
                  newMsg.id
                );
                updatedMessages[optimisticIndex] = newMsg;
                changesMade = true;
              } else {
                updatedMessages.push(newMsg);
                changesMade = true;
              }
            });

            if (changesMade) {
              console.log("✅ Updated messages list with fetched history");
              return updatedMessages.sort(
                (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
              );
            }

            return prevMessages;
          });
        }
      }
    } catch (error) {
      console.warn("⚠️ Failed to fetch missed messages:", error);
    }
  };

  // Handle incoming messages from bot
  const handleIncomingMessage = useCallback((msg) => {
    console.log("📨 Incoming message received:", {
      id: msg._id,
      type: msg.content?.type,
      from: msg?.content?.data?.from,
      messageFrom: msg?.messageFrom,
      senderId: msg?.senderId,
      recipientId: msg?.recipientId
    });

    const newMessage = processMessage(msg);

    console.log("🔍 Processed message:", {
      type: newMessage.type,
      content: newMessage.content?.substring(0, 30)
    });

    // ── CONVERSATION MEMBERSHIP GUARD ─────────────────────────────────────
    // Drop messages that don't belong to the active or awaited conversation.
    // This prevents delayed socket events from an old chat from being rendered
    // after the user clicks Home/New Chat or switches conversations.
    const msgConvId =
      msg.vcxConversationId ||
      msg.vcx_conversation_id ||
      msg.conversationId ||
      msg.conversation_id ||
      msg.wcconversationId ||
      msg.wc_conversation_id ||
      "";
    const current = userSessionRef.current;
    const activeConversationId =
      current.vcxConversationId || current.conversationId || current.internalConversationId || "";
    const awaitedConversationId = awaitingConversationRef.current;

    const isRelevantConversation =
      !msgConvId ||
      (activeConversationId && msgConvId === activeConversationId) ||
      (awaitedConversationId && msgConvId === awaitedConversationId);

    if (msgConvId && activeConversationId && msgConvId !== activeConversationId && msgConvId !== awaitedConversationId) {
      console.log("⏭️ Ignoring message from non-active conversation:", msgConvId);
      return;
    }

    if (msgConvId && !activeConversationId && sessionResetRef.current && msgConvId !== awaitedConversationId) {
      console.log("⏭️ Ignoring delayed old conversation message after reset:", msgConvId);
      return;
    }

    // Explicitly reject messages from conversations that were closed by resetChat,
    // even after sessionResetRef has been cleared by a new sendMessage call.
    if (msgConvId && closedConversationIdsRef.current.has(msgConvId) && msgConvId !== awaitedConversationId) {
      console.log("⏭️ Ignoring message from closed conversation:", msgConvId);
      return;
    }
    // ── END CONVERSATION MEMBERSHIP GUARD ─────────────────────────────────

    // ── EXECUTION ID INTERCEPTION (must be before typing resolution) ───────────
    const rawContent = newMessage.content || '';
    if (rawContent.startsWith('__EXEC_ID__:')) {
      const execId = rawContent.replace('__EXEC_ID__:', '').trim();
      if (execId) {
        pendingExecutionIdRef.current = execId;
        // If edit was requested before executionId arrived, cancel immediately
        if (cancelOnNextExecIdRef.current) {
          cancelOnNextExecIdRef.current = false;
          console.log('🛑 Auto-cancelling old execution after edit:', execId);
          fetch('https://workflow.igr-consulting.com/webhook/stop-execution', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'stop_requested', payload: { type: 'stop_requested', executionId: execId } }),
          }).catch(e => console.warn('⚠️ Auto-stop failed:', e.message));
          pendingExecutionIdRef.current = null;
          return;
        }
        setCanStop(true);
        console.log('🆔 Stored n8n executionId:', execId);
      }
      return;
    }
    // ── END EXECUTION ID INTERCEPTION ─────────────────────────────────────────

    // ── TYPING RESOLUTION ─────────────────────────────────────────────────
    // For the Botpress -> n8n async flow, stop thinking only when a real
    // assistant response for the current/awaited conversation actually arrives.
    // User echoes and unrelated lifecycle events must not clear the animation.
    const isAssistantResponse = !isUserSideMessage(msg);
    if (isAssistantResponse && isRelevantConversation) {
      if (deductionTimeoutRef.current) {
        clearTimeout(deductionTimeoutRef.current);
        deductionTimeoutRef.current = null;
      }
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      awaitingBotResponseRef.current = false;
      setResponseDelayed(false);
      setIsTyping(false);
      console.log("✅ Assistant response arrived — stopping typing animation");
    }
    // ── END TYPING RESOLUTION ─────────────────────────────────────────────

    // ── POST PREVIEW INTERCEPTION ─────────────────────────────────────────
    if (tryDispatchPostPreview(rawContent, msg._id || newMessage.id)) {
      // Always return — never show this raw message in the chat
      return;
    }
    // ── END POST PREVIEW INTERCEPTION ────────────────────────────────────

    // Sync conversation id from incoming message if it carries a CONVO_ id
    // different from the current session. This ensures bot responses that were
    // saved under a freshly-generated conversation id are loaded correctly on refresh.
    // Guard: never overwrite an empty session with an old CONVO_ id — that would
    // hijack a freshly-reset session and merge the new chat into the old conversation.
    if (msgConvId && String(msgConvId).startsWith("CONVO_") && !sessionResetRef.current) {
      const hasActiveConversation = current.conversationId || current.vcxConversationId || current.internalConversationId;
      if (hasActiveConversation && msgConvId !== current.conversationId && msgConvId !== current.vcxConversationId) {
        console.log("🔄 Syncing vcxConversationId from incoming message:", msgConvId);
        const updated = { ...current, conversationId: msgConvId, vcxConversationId: msgConvId };
        userSessionRef.current = updated;
        setUserSession(updated);
      }
    }

    if (newMessage.type === "user") {
      console.log("🚫 Skipping user message echo");
      return;
    }

    setMessages((prev) => {
      const now = new Date(newMessage.timestamp).getTime();
      const isDuplicate = prev.some(existingMsg => {
        const timeDiff = Math.abs(now - new Date(existingMsg.timestamp).getTime());
        const contentMatch = existingMsg.content === newMessage.content;
        const typeMatch = existingMsg.type === newMessage.type;
        return contentMatch && typeMatch && timeDiff < 10000;
      });

      if (isDuplicate) {
        console.log("🚫 Skipping duplicate message");
        return prev;
      }

      const lastMsg = prev[prev.length - 1];
      if (
        lastMsg &&
        lastMsg.type === newMessage.type &&
        lastMsg.type === "assistant" &&
        lastMsg.content === newMessage.content &&
        (newMessage.id !== lastMsg.id)
      ) {
        console.log("🚫 Ignoring consecutive duplicate bot message");
        return prev;
      }

      console.log("✅ Adding new message to chat");
      return [...prev, newMessage];
    });

    const currentSession = userSessionRef.current;
    if (currentSession.conversationId && newMessage.content) {
      updateChatHistory(currentSession.conversationId, newMessage.content);

      try {
        const historyKey = `chatHistory_${WEBCHAT_CONFIG.clientName}`;
        const storedHistory = localStorage.getItem(historyKey);
        if (storedHistory) {
          const chatHistory = JSON.parse(storedHistory);
          const chatIndex = chatHistory.findIndex(c => c.conversationKey === currentSession.conversationId);

          if (chatIndex !== -1) {
            const chat = chatHistory[chatIndex];
            if ((!chat.title || chat.title === "New Conversation") && newMessage.content && newMessage.content.trim().length > 0) {
              const newTitle = newMessage.content.length > 50 ? newMessage.content.substring(0, 50) + '…' : newMessage.content;
              const uuid = WEBCHAT_CONFIG.uuid;
              const token = userTokenRef.current;

              if (token) {
                addConversationTitle(currentSession.conversationId, uuid, newTitle, token).then(res => {
                  if (res.success) {
                    chatHistory[chatIndex].title = newTitle;
                    localStorage.setItem(historyKey, JSON.stringify(chatHistory));
                    console.log("✅ Title updated to:", newTitle);
                  }
                });
              }
            }
          }
        }
      } catch (e) {
        console.error("Error updating title:", e);
      }
    }

    if (!isUserSideMessage(msg)) {
      const token = userTokenRef.current;
      const userId = userSessionRef.current?.userId;

      if (token && userId) {
        customerAPI.getCredits(token, userId).then(res => {
          if (res.success) {
            const data = res.data?.data || res.data;
            if (data && data.extraCharRequired > 0 && (data.creditsRemaining === undefined || data.creditsRemaining <= 0)) {
              console.log("⚠️ Message needs blurring!", data);

              setMessages(prev => {
                const updated = [...prev];
                const targetIndex = updated.findIndex(m => m.id === newMessage.id);
                if (targetIndex !== -1) {
                  updated[targetIndex] = {
                    ...updated[targetIndex],
                    blurInfo: {
                      required: data.extraCharRequired,
                      total: data.lastOutputChars || 0
                    }
                  };
                  return updated;
                }
                return prev;
              });
            }
          }
        }).catch(err => console.error("Error checking credits for blur:", err));
      }
    }
  }, [tryDispatchPostPreview]);

  const performFetch = async (payload) => {
    return fetch(
      `${CHAT_API}/wc-webhook/recieve-webchat-message/${WEBCHAT_CONFIG.clientId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
  };

  const checkCreditsAndBlock = async () => {
    const token = userTokenRef.current;
    let currentUserId = userSessionRef.current?.userId;
    if (!currentUserId && user?.userId) {
      currentUserId = user.userId;
    }

    if (!token || !currentUserId) {
      return { allowed: true };
    }

    try {
      console.log("💰 Checking credits before sending message for user:", currentUserId);
      const res = await customerAPI.getCredits(token, currentUserId);

      if (res.success && res.data) {
        const creditsData = res.data.data || res.data;
        const creditsRemaining = creditsData.creditsRemaining;
        const isPlanExpired = creditsData.planExpired;

        console.log("DEBUG: Parsed creditsRemaining:", creditsRemaining, "planExpired:", isPlanExpired);

        if (isPlanExpired === true) {
          console.error("🚫 Plan Expired! Blocking message with delay.");
          return { allowed: false, reason: "plan_expired" };
        }

        if (creditsRemaining !== undefined && creditsRemaining <= 0) {
          console.error("🚫 Insufficient credits (Pre-Check)! Blocking message.");
          setInsufficientCredits(true);
          return { allowed: false, reason: "insufficient_credits" };
        } else {
          setInsufficientCredits(false);
          return { allowed: true };
        }
      } else {
        console.warn("⚠️ Credit check failed (API error), allowing message for now.");
        return { allowed: true };
      }
    } catch (e) {
      console.error("⚠️ Credit check exception:", e);
      return { allowed: true };
    }
  };

  // Send message to bot
  const sendMessage = async (
    messageText,
    messageType = "text",
    additionalData = {}
  ) => {
    if (!messageText.trim() && messageType === "text") return;

    const creditStatus = await checkCreditsAndBlock();
    if (creditStatus && creditStatus.allowed === false) {
      if (creditStatus.reason === "plan_expired") {
        const userMessage = {
          id: Date.now(),
          type: "user",
          content: messageText,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setIsTyping(true);

        setTimeout(() => {
          setIsTyping(false);
          setPlanExpired(true);
        }, 4500);
      }
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsTyping(true);
    setCanStop(false);
    setResponseDelayed(false);
    console.log("UseWebChat: Force setIsTyping(true) on send");

    // Mark that we are waiting for the real assistant response. This prevents
    // Botpress lifecycle events like "typing stopped" from killing the animation
    // while n8n is still processing asynchronously.
    awaitingBotResponseRef.current = true;
    awaitingConversationRef.current =
      userSessionRef.current.vcxConversationId ||
      userSessionRef.current.conversationId ||
      userSessionRef.current.internalConversationId ||
      "";

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
    }
    responseTimeoutRef.current = setTimeout(() => {
      if (awaitingBotResponseRef.current) {
        setResponseDelayed(true);
        // Keep the typing animation alive — n8n may still be working.
        console.log("⏰ Response delayed but still awaiting n8n response");
      }
    }, 60000);

    try {
      // The user intentionally sent a message, so a reset is no longer in flight.
      sessionResetRef.current = false;

      const currentSession = userSessionRef.current;

      let conversationIdToSend = currentSession.conversationId || "";
      let userIdToSend = currentSession.userId || "";
      let userTokenToSend = currentSession.userToken || "";
      let vcxConversationIdToSend = currentSession.vcxConversationId || "";
      let internalConversationIdToSend = currentSession.internalConversationId || "";

      // Only recover user identity from localStorage. Never recover conversation
      // ids here — an empty conversation id is how a brand-new chat is started.
      if (!conversationIdToSend || !userIdToSend) {
        console.warn("⚠️ sendMessage: Missing conversationId or userId. Attempting final recovery...");
        const stored = localStorage.getItem(WEBCHAT_CONFIG.clientName);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.userId) userIdToSend = parsed.userId;
          if (parsed.userToken) userTokenToSend = parsed.userToken;
        }
      }

      // If it's a CONVO_ ID, swap to the internal backend conv_ ID when available
      // for conversation_id, but keep vcxConversationId as the CONVO_ id so the
      // backend can continue the same conversation.
      if (conversationIdToSend && conversationIdToSend.startsWith("CONVO_")) {
        if (internalConversationIdToSend && internalConversationIdToSend.startsWith("conv_")) {
          console.log("🔄 Using internal conv_ ID for conversation_id:", internalConversationIdToSend);
          conversationIdToSend = internalConversationIdToSend;
        } else {
          console.log("⚠️ Detected CONVO_ ID as conversationId. Clearing conversation_id (backend expects conv_ or empty).");
          conversationIdToSend = "";
        }
      }

      if (!conversationIdToSend || conversationIdToSend === "") {
        console.log("⚠️ No valid conversation ID found.");

        if (!userIdToSend && userTokenRef.current) {
          try {
            const decoded = jwtDecode(userTokenRef.current);
            if (decoded.userId) userIdToSend = decoded.userId;
          } catch (e) { }
        }

        if (!userIdToSend && user?.userId) {
          userIdToSend = user.userId;
        }

        if (!userIdToSend) {
          console.error("🚫 BLOCKED: Attempting to send message without UserId.");
          if (user) {
            notifyError("Chat session invalid. Reloading...");
            return;
          }
        }
      }

      const payload = {
        conversation: {
          userId: userIdToSend,
          conversation_id: conversationIdToSend,
          userToken: userTokenToSend,
        },
        message: messageText,
        toggle_status: chatStatus,
        clientId: WEBCHAT_CONFIG.uuid,
        webhookId: WEBCHAT_CONFIG.webhookId,
        vcxConversationId: vcxConversationIdToSend,
      };

      if (externalId.current) {
        payload.external_id = externalId.current;
      }

      console.log("📤 Sending message with payload:", payload);

      const response = await performFetch(payload);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log("✅ Response received:", result);

      const resRoot = result.data?.res || result.data || result;
      const vcxConversationId =
        result.data?.vcxConversationId ||
        result.data?.vcx_conversation_id ||
        resRoot.vcxConversationId ||
        resRoot.vcx_conversation_id ||
        "";

      const nextUserId = resRoot.user_id || currentSession.userId;
      const nextConversationId = resRoot.conversation_id || currentSession.conversationId;
      const nextUserToken = resRoot.user_token || currentSession.userToken;
      const nextDisplayConversationId =
        vcxConversationId ||
        (String(currentSession.conversationId || "").startsWith("CONVO_") ? currentSession.conversationId : "") ||
        nextConversationId;

      let shouldPersist = false;

      let responseMessageText = "";

      if (resRoot.output && Array.isArray(resRoot.output) && resRoot.output.length > 0) {
        const firstOutput = resRoot.output[0];
        if (firstOutput.content && Array.isArray(firstOutput.content) && firstOutput.content.length > 0) {
          responseMessageText = firstOutput.content[0].text || "";
        }
      } else if (resRoot.message) {
        responseMessageText = resRoot.message;
      } else if (result.content?.data?.body) {
        responseMessageText = result.content.data.body;
      }

      if (resRoot.output && Array.isArray(resRoot.output) && responseMessageText) {
        console.log("📝 Detected output format in sendMessage. Setting title to:", responseMessageText);

        if (nextDisplayConversationId) {
          addConversationTitle(nextDisplayConversationId, WEBCHAT_CONFIG.uuid, responseMessageText)
            .then(res => {
              if (res.success) {
                updateChatHistoryTitle(nextDisplayConversationId, responseMessageText);
              }
            })
            .catch(err => console.error("❌ Failed to set title in sendMessage:", err));
        }
      }

      if (!currentSession.userId && nextUserId) {
        console.log("✅ First message - userId captured:", nextUserId);
        firstMessageSentRef.current = true;
        shouldPersist = true;

        if (socketRef.current) {
          socketRef.current.emit("subscribe", nextUserId);
        }
      }

      if (nextConversationId && nextConversationId !== currentSession.conversationId) {
        console.log("🔄 Conversation ID updated:", nextConversationId);
        shouldPersist = true;

        if (!currentSession.conversationId || currentSession.conversationId === "") {
          saveChatToHistory(nextDisplayConversationId, nextUserId || currentSession.userId, messageText);
        } else {
          renameChatHistoryKey(currentSession.conversationId, nextDisplayConversationId);
        }
      }

      if (nextUserToken && nextUserToken !== currentSession.userToken) {
        console.log("🔄 User token updated");
        shouldPersist = true;
      }

      if (vcxConversationId && vcxConversationId !== currentSession.vcxConversationId) {
        console.log("🔄 vcxConversationId updated:", vcxConversationId);
        shouldPersist = true;
      }

      if (shouldPersist) {
        const updatedSession = {
          userId: nextUserId,
          userToken: nextUserToken,
          webchatId: nextUserId,
          conversationId: nextDisplayConversationId,
          vcxConversationId: vcxConversationId || currentSession.vcxConversationId,
          internalConversationId: nextConversationId || currentSession.internalConversationId,
        };

        userSessionRef.current = updatedSession;
        setUserSession(updatedSession);
        sessionResetRef.current = false;

        // New conversation is established — previous closed IDs are no longer needed.
        closedConversationIdsRef.current.clear();

        // The response created/confirmed the conversation; update what we are
        // awaiting so the next socket message can be matched correctly.
        awaitingConversationRef.current =
          updatedSession.vcxConversationId ||
          updatedSession.conversationId ||
          updatedSession.internalConversationId ||
          "";

        console.log("💾 Session persisted to localStorage:", updatedSession);

        if (!window.__didInitialFetch) {
          fetchAndAppendMissedMessages();
          window.__didInitialFetch = true;
        }
      }

      if (nextDisplayConversationId && nextDisplayConversationId !== "") {
        updateChatHistory(nextDisplayConversationId, messageText);
      }

    } catch (error) {
      console.error("❌ Error sending message:", error);
      awaitingBotResponseRef.current = false;
      setIsTyping(false);
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
    }
  };

  // Regenerate a previous assistant response by resending the preceding user message
  const regenerateResponse = async (assistantMessageId) => {
    const assistantIndex = messages.findIndex((m) => m.id === assistantMessageId);
    if (assistantIndex <= 0) return;

    // Find the immediately preceding user message
    let userIndex = assistantIndex - 1;
    while (userIndex >= 0 && messages[userIndex].type !== "user") {
      userIndex--;
    }
    if (userIndex < 0) return;

    const userMessage = messages[userIndex];

    // If a response is currently being generated, stop the old workflow first
    if (awaitingBotResponseRef.current && pendingExecutionIdRef.current) {
      await stopResponse();
    } else if (awaitingBotResponseRef.current && !pendingExecutionIdRef.current) {
      // executionId not yet received — set flag so it gets cancelled when it arrives
      cancelOnNextExecIdRef.current = true;
      setCanStop(false);
      setIsTyping(false);
      awaitingBotResponseRef.current = false;
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
    }

    setResponseDelayed(false);

    // Resend the user message — keeps all existing messages and appends a new user bubble + response
    sendMessage(userMessage.content);
  };

  const editAndResend = async (userMessageId, newText) => {
    if (!newText.trim()) return;
    const userIndex = messages.findIndex((m) => m.id === userMessageId);
    if (userIndex < 0) return;

    // If a response is currently being generated, stop the old workflow first
    if (awaitingBotResponseRef.current && pendingExecutionIdRef.current) {
      await stopResponse();
    } else if (awaitingBotResponseRef.current && !pendingExecutionIdRef.current) {
      // executionId not yet received — set flag so it gets cancelled when it arrives
      cancelOnNextExecIdRef.current = true;
      setCanStop(false);
      setIsTyping(false);
      awaitingBotResponseRef.current = false;
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
    }

    // Replace the user message text and drop everything after it
    setMessages((prev) =>
      prev
        .slice(0, userIndex)
        .concat({ ...prev[userIndex], content: newText })
    );
    setResponseDelayed(false);

    sendMessage(newText);
  };

  const sendQuickReply = async (title, payload) => {
    const creditStatus = await checkCreditsAndBlock();
    if (creditStatus && creditStatus.allowed === false) {
      if (creditStatus.reason === "plan_expired") {
        const userMessage = { id: Date.now(), type: "user", content: title, timestamp: new Date() };
        setMessages((prev) => [...prev, userMessage]);
        setIsTyping(true);
        setTimeout(() => { setIsTyping(false); setPlanExpired(true); }, 4500);
      }
      return;
    }

    setIsTyping(true);
    setResponseDelayed(false);

    awaitingBotResponseRef.current = true;
    awaitingConversationRef.current =
      userSessionRef.current.vcxConversationId ||
      userSessionRef.current.conversationId ||
      userSessionRef.current.internalConversationId ||
      "";

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
    }
    responseTimeoutRef.current = setTimeout(() => {
      if (awaitingBotResponseRef.current) {
        setResponseDelayed(true);
        console.log("⏰ Quick reply delayed but still awaiting n8n response");
      }
    }, 60000);

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: title,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      sessionResetRef.current = false;

      const currentSession = userSessionRef.current;

      let conversationIdToSend = currentSession.conversationId || "";
      let vcxConversationIdToSend = currentSession.vcxConversationId || "";
      let userIdToSend = currentSession.userId || "";
      let userTokenToSend = currentSession.userToken || "";
      let internalConversationIdToSend = currentSession.internalConversationId || "";

      if (conversationIdToSend && conversationIdToSend.startsWith("CONVO_")) {
        if (internalConversationIdToSend && internalConversationIdToSend.startsWith("conv_")) {
          console.log("🔄 Quick reply: using internal conv_ ID:", internalConversationIdToSend);
          conversationIdToSend = internalConversationIdToSend;
        } else {
          conversationIdToSend = "";
        }
      }

      if (!conversationIdToSend || conversationIdToSend === "") {
        conversationIdToSend = "";
      }

      const messagePayload = {
        conversation: {
          userId: userIdToSend,
          conversation_id: conversationIdToSend,
          userToken: userTokenToSend,
        },
        message: title,
        payload: payload,
        toggle_status: chatStatus,
        clientId: WEBCHAT_CONFIG.uuid,
        webhookId: WEBCHAT_CONFIG.webhookId,
        vcxConversationId: vcxConversationIdToSend,
      };

      if (externalId.current) {
        messagePayload.external_id = externalId.current;
      }

      const response = await performFetch(messagePayload);

      if (response.ok) {
        const result = await response.json();
        const resRoot = result.data?.res || result.data || result;

        const nextConversationId = resRoot.conversation_id || currentSession.conversationId;

        const nextVcxId = result.data?.vcxConversationId;

        if (nextConversationId && (nextConversationId !== currentSession.conversationId || nextConversationId !== currentSession.internalConversationId)) {
          const updatedSession = {
            ...userSessionRef.current,
            conversationId: nextVcxId || currentSession.conversationId || nextConversationId,
            vcxConversationId: nextVcxId || userSessionRef.current.vcxConversationId,
            internalConversationId: nextConversationId,
          };
          userSessionRef.current = updatedSession;
          setUserSession(updatedSession);
          sessionResetRef.current = false;
          awaitingConversationRef.current =
            updatedSession.vcxConversationId ||
            updatedSession.conversationId ||
            updatedSession.internalConversationId ||
            "";

          if (!currentSession.conversationId || currentSession.conversationId === "") {
            saveChatToHistory(nextConversationId, currentSession.userId, title);
          } else {
            renameChatHistoryKey(currentSession.conversationId, nextConversationId);
          }
        }

        if (nextConversationId && nextConversationId !== "") {
          updateChatHistory(nextVcxId || nextConversationId, title);
        }
      }
    } catch (error) {
      console.error("❌ Error sending quick reply:", error);
      awaitingBotResponseRef.current = false;
      setIsTyping(false);
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
    }
  };

  const submitForm = async (formData, formId) => {
    const creditStatus = await checkCreditsAndBlock();
    if (creditStatus && creditStatus.allowed === false) {
      if (creditStatus.reason === "plan_expired") {
        setIsTyping(true);
        setTimeout(() => { setIsTyping(false); setPlanExpired(true); }, 4500);
      }
      return;
    }

    const currentSession = userSessionRef.current;

    let conversationIdToSend = currentSession.conversationId || "";
    let vcxConversationIdToSend = currentSession.vcxConversationId || "";
    let userIdToSend = currentSession.userId || "";
    let userTokenToSend = currentSession.userToken || "";
    let internalConversationIdToSend = currentSession.internalConversationId || "";

    if (conversationIdToSend && conversationIdToSend.startsWith("CONVO_")) {
      if (internalConversationIdToSend && internalConversationIdToSend.startsWith("conv_")) {
        console.log("🔄 Form submit: using internal conv_ ID:", internalConversationIdToSend);
        conversationIdToSend = internalConversationIdToSend;
      } else {
        conversationIdToSend = "";
      }
    }

    if (!conversationIdToSend || conversationIdToSend === "") {
      conversationIdToSend = "";
    }

    const payload = {
      conversation: {
        userId: userIdToSend,
        conversation_id: conversationIdToSend,
        userToken: userTokenToSend,
      },
      vcxConversationId: vcxConversationIdToSend,
      message: {
        type: "formdata",
        data: {
          formfields: formData,
          form_sms_id: formId,
          formstatus: true,
        },
      },
      toggle_status: "Human",
      clientId: WEBCHAT_CONFIG.uuid,
      webhookId: WEBCHAT_CONFIG.webhookId,
    };

    if (externalId.current) {
      payload.external_id = externalId.current;
    }

    try {
      sessionResetRef.current = false;

      setIsTyping(true);
      setResponseDelayed(false);

      awaitingBotResponseRef.current = true;
      awaitingConversationRef.current =
        userSessionRef.current.vcxConversationId ||
        userSessionRef.current.conversationId ||
        userSessionRef.current.internalConversationId ||
        "";

      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
      responseTimeoutRef.current = setTimeout(() => {
        if (awaitingBotResponseRef.current) {
          setResponseDelayed(true);
          console.log("⏰ Form submit delayed but still awaiting response");
        }
      }, 60000);

      const response = await performFetch(payload);

      if (response.ok) {
        const result = await response.json();
        const resRoot = result.data?.res || result.data || result;

        const successMessage = {
          id: Date.now(),
          type: "user",
          content: "Form Submitted Successfully!",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, successMessage]);

        const nextConversationId = resRoot.conversation_id || currentSession.conversationId;
        const nextVcxId = result.data?.vcxConversationId;
        if (nextConversationId && (nextConversationId !== currentSession.conversationId || nextConversationId !== currentSession.internalConversationId)) {
          const updatedSession = {
            ...userSessionRef.current,
            conversationId: nextVcxId || currentSession.conversationId || nextConversationId,
            vcxConversationId: nextVcxId || userSessionRef.current.vcxConversationId,
            internalConversationId: nextConversationId,
          };
          userSessionRef.current = updatedSession;
          setUserSession(updatedSession);
          sessionResetRef.current = false;
          awaitingConversationRef.current =
            updatedSession.vcxConversationId ||
            updatedSession.conversationId ||
            updatedSession.internalConversationId ||
            "";

          if (!currentSession.conversationId || currentSession.conversationId === "") {
            saveChatToHistory(nextVcxId || nextConversationId, currentSession.userId, "Form submitted");
          } else {
            renameChatHistoryKey(currentSession.conversationId, nextVcxId || nextConversationId);
          }
        }

        if (nextConversationId && nextConversationId !== "") {
          updateChatHistory(nextConversationId, "Form submitted");
        }
      }
      awaitingBotResponseRef.current = false;
      setIsTyping(false);
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      awaitingBotResponseRef.current = false;
      setIsTyping(false);
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
    }
  };

  const handlePostback = useCallback((value) => {
    console.log("Postback action triggered:", value);
    setIsTyping(true);
    fetch("/postback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: value }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Postback response:", data);
        setIsTyping(false);
      })
      .catch((error) => {
        console.error("Error handling postback:", error);
        setIsTyping(false);
      });
  }, []);

  // Reset the chat session (used by Home and New Chat).
  // This closes the current conversation on the backend and resets local state,
  // but it does NOT delete messages and does NOT create a new backend conversation.
  // The new conversation is created lazily when the user sends the first message.
  const resetChat = async () => {
    const currentSession = userSessionRef.current;
    sessionResetVersionRef.current += 1;
    console.log("🆕 Resetting chat/Restarting...");

    // Capture the ids we need to close on the backend BEFORE we clear them.
    const sessionToClose = {
      userId: currentSession.userId,
      conversationId: currentSession.conversationId,
      vcxConversationId: currentSession.vcxConversationId,
      conversationName: currentSession.conversationName,
      userToken: currentSession.userToken,
    };

    try {
      setIsTyping(false);
      setResponseDelayed(false);
      setMessages([]);
      firstMessageSentRef.current = false;
      window.__didInitialFetch = false;
      if (deductionTimeoutRef.current) {
        clearTimeout(deductionTimeoutRef.current);
        deductionTimeoutRef.current = null;
      }
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }

      // Clear conversation IDs IMMEDIATELY so any prompt clicked right after
      // Home/New Chat starts a fresh conversation. The backend close is fired
      // afterwards without blocking the UI.
      const clearedSession = {
        ...currentSession,
        conversationId: "",
        vcxConversationId: "",
        internalConversationId: "",
        conversationName: "",
      };
      userSessionRef.current = clearedSession;
      setUserSession(clearedSession);
      localStorage.setItem(WEBCHAT_CONFIG.clientName, JSON.stringify(clearedSession));
      sessionResetRef.current = true;
      awaitingBotResponseRef.current = false;
      awaitingConversationRef.current = "";

      // Record the closed IDs so delayed socket messages from the old conversation
      // are explicitly rejected even after sessionResetRef is cleared by sendMessage.
      if (currentSession.vcxConversationId) closedConversationIdsRef.current.add(currentSession.vcxConversationId);
      if (currentSession.conversationId) closedConversationIdsRef.current.add(currentSession.conversationId);
      if (currentSession.internalConversationId) closedConversationIdsRef.current.add(currentSession.internalConversationId);

      console.log("✅ Chat reset locally.");

      // Reset dispatched post preview IDs for the new conversation
      dispatchedPostPreviewIds.current.clear();

      // Close the previous conversation on the backend so the next message does
      // not append to it. We do NOT delete messages here. Fire-and-forget: the
      // UI has already been cleared above.
      if (sessionToClose.userId && sessionToClose.conversationId && WEBCHAT_CONFIG.uuid) {
        const user = localStorage.getItem("user");
        const userEmail = user ? JSON.parse(user).email : "user@example.com";
        const targetId = sessionToClose.vcxConversationId || sessionToClose.conversationId;
        closeChat(
          userEmail,
          WEBCHAT_CONFIG.uuid,
          sessionToClose.userId,
          targetId,
          sessionToClose.conversationName || "Your Conversation",
          WEBCHAT_CONFIG.webhookId,
          sessionToClose.userToken
        )
          .then(() => {
            console.log("✅ resetChat: closed previous conversation:", targetId);
          })
          .catch((closeErr) => {
            console.warn("⚠️ resetChat: failed to close previous conversation:", closeErr);
          });
      }

      // ⚠️ FIX #3: Clear all pending timeouts to prevent old conversation state from firing
      if (deductionTimeoutRef.current) {
        clearTimeout(deductionTimeoutRef.current);
        deductionTimeoutRef.current = null;
      }
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }

      if (socketRef.current && sessionToClose.userId) {
        socketRef.current.emit("subscribe", sessionToClose.userId);
      }

    } catch (error) {
      console.error("❌ Error resetting chat:", error);
    }
  };

  // Hard-delete Botpress messages for the current user.
  // This is only called from the explicit Delete button in the history sidebar.
  const deleteBotpressMessages = async (userId) => {
    if (!userId) return;
    try {
      await fetch(`${CHAT_API}/chat/delete-botpress-messages/${userId}/${WEBCHAT_CONFIG.uuid}`, {
        method: "DELETE",
      });
      console.log("✅ Called delete-botpress-messages");
    } catch (error) {
      console.error("❌ Error deleting botpress messages:", error);
    }
  };

  const loadMessages = (messagesArray) => {
    console.log("📥 Loading messages:", messagesArray?.length || 0);
    if (messagesArray && Array.isArray(messagesArray)) {
      setMessages(messagesArray);
      console.log("✅ Messages loaded successfully");
    }
  };

  const loadMessagesFromHistory = async (conversationKey, page = 1) => {
    if (!conversationKey) {
      console.error("❌ No conversation key provided");
      return { success: false, error: "No conversation key provided" };
    }

    // ⚠️ FIX #2, #3, #4: Clear old state when loading an old conversation
    // Clear pending timeouts to prevent old conversation state from interfering
    if (deductionTimeoutRef.current) {
      clearTimeout(deductionTimeoutRef.current);
      deductionTimeoutRef.current = null;
      console.log("🧹 Cleared deductionTimeout for old conversation");
    }
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
      console.log("🧹 Cleared responseTimeout for old conversation");
    }

    // Reset window globals to allow proper message fetching
    window.__didInitialFetch = false;
    console.log("🧹 Reset window.__didInitialFetch for old conversation");

    // Force setIsTyping to false and clear delayed response state
    setIsTyping(false);
    setResponseDelayed(false);
    setIsHistoryLoading(true);
    console.log("🧹 Reset typing and response delay states");

    try {
      console.log("📥 Loading messages from chat history API...");
      console.log("🔑 Conversation key:", conversationKey);

      const user = localStorage.getItem("user");
      const userId =
        userSession.userId ||
        (user ? JSON.parse(user).email : "user@example.com");

      // ── ID resolution ────────────────────────────────────────────────────
      // /chat/get-messages queries MongoDB by `conversationId`, which is ALWAYS
      // a CONVO_ id. The sidebar, however, stores the webchat `conv_...` id
      // (stored on messages as `wcconversationId`). Passing a conv_ id to
      // /get-messages returns []. So we MUST resolve a conv_ id to its CONVO_ id
      // before calling /get-messages.
      const keyStr = String(conversationKey || "");
      let convoId = keyStr.startsWith("CONVO_") ? keyStr : "";        // for /get-messages + conversationId match
      let clickedConvId = keyStr.startsWith("conv_") ? keyStr : "";   // webchat wcconversationId match

      // ── Step 1: fetch the user-scoped message list ───────────────────────
      // This endpoint returns EVERY message for the user and each message carries
      // BOTH `conversationId` (CONVO_) and `wcconversationId` (conv_), plus the
      // full message body. We use it (a) to resolve conv_ -> CONVO_ and (b) as a
      // guaranteed fallback source of messages for the clicked conversation.
      let allUserScoped = [];
      try {
        const userScopedRes = await fetch(
          `${CHAT_API}/chat/get-botpress-messages/${userId}/${WEBCHAT_CONFIG.clientId}`
        );
        if (userScopedRes.ok) {
          const userScopedData = await userScopedRes.json();
          allUserScoped =
            userScopedData.message ||
            userScopedData.messages ||
            userScopedData.data ||
            [];
          console.log("📦 user-scoped messages fetched:", allUserScoped.length);
        } else {
          console.warn("⚠️ user-scoped fetch status:", userScopedRes.status);
        }
      } catch (e) {
        console.warn("⚠️ User-scoped message fetch failed (history):", e);
      }

      // ── Step 2: resolve conv_ -> CONVO_ when needed ──────────────────────
      if (!convoId && clickedConvId) {
        const match = allUserScoped.find(
          (m) => (m.wcconversationId || m.wc_conversation_id || "") === clickedConvId
        );
        if (match) {
          const resolved =
            match.conversationId ||
            match.vcxConversationId ||
            match.vcx_conversation_id ||
            "";
          if (String(resolved).startsWith("CONVO_")) {
            convoId = resolved;
            console.log("🔁 Resolved conv_ → CONVO_:", clickedConvId, "→", convoId);
          }
        }
        if (!convoId) {
          console.warn(
            "⚠️ Could not resolve a CONVO_ id for", clickedConvId,
            "— will render from user-scoped fallback only"
          );
        }
      }

      // ── Step 3: fetch canonical messages via /get-messages (CONVO_ only) ─
      let apiMessages = [];
      if (convoId) {
        const result = await getChatHistory(convoId, userId, WEBCHAT_CONFIG.uuid, page);
        if (result.success && result.data) {
          apiMessages = result.data.messages || result.data.data || [];
          console.log("📦 get-messages (/chat/get-messages) raw response:", {
            convoId,
            messageCount: apiMessages.length,
          });
        } else {
          console.warn("⚠️ /get-messages failed for", convoId, result.error);
        }
      } else {
        // Never call /get-messages with a conv_ id — it always returns [].
        console.warn("⚠️ No CONVO_ id available; skipping /get-messages for:", conversationKey);
      }

      // ── Step 4: keep only messages that belong to the CLICKED conversation ─
      // The sidebar's identity for an item is the key that was clicked:
      //   • conv_ (wcconversationId) → the per-conversation id. NOTE: several conv_
      //     ids can share ONE CONVO_ id in the DB, so matching by CONVO_ would merge
      //     unrelated sidebar chats. Prefer the exact conv_ match.
      //   • CONVO_ → match by conversationId (the whole CONVO_ thread).
      // Apply the SAME predicate to both sources so /get-messages (which returns the
      // whole CONVO_ thread) cannot pull in sibling conversations.
      const belongsToClicked = (msg) => {
        const msgWc = msg.wcconversationId || msg.wc_conversation_id || "";
        const msgConv =
          msg.conversationId ||
          msg.conversation_id ||
          msg.vcxConversationId ||
          msg.vcx_conversation_id ||
          "";
        if (clickedConvId) return msgWc === clickedConvId;
        if (convoId) return msgConv === convoId;
        return false;
      };

      const conversationApiMessages = apiMessages.filter(belongsToClicked);
      const userScopedMessages = allUserScoped.filter(belongsToClicked);

      console.log("📦 message sources (filtered to clicked conversation):", {
        fromGetMessages: conversationApiMessages.length,
        fromUserScoped: userScopedMessages.length,
        convoId,
        clickedConvId,
      });

      // ── Step 5: merge + dedupe by _id ────────────────────────────────────
      const mergedMap = new Map();
      [...conversationApiMessages, ...userScopedMessages].forEach((msg) => {
        if (msg && msg._id) mergedMap.set(msg._id, msg);
      });
      const uniqueRawMessages = Array.from(mergedMap.values());

      // ── Step 6: derive canonical ids for the session ─────────────────────
      const primaryConversationId = convoId || "";
      let internalConversationId = clickedConvId || "";
      if (!internalConversationId) {
        const withWc = uniqueRawMessages.find((m) =>
          String(m.wcconversationId || m.wc_conversation_id || "").startsWith("conv_")
        );
        if (withWc) {
          internalConversationId =
            withWc.wcconversationId || withWc.wc_conversation_id || "";
        }
      }

      const sessionNeedsUpdate =
        (primaryConversationId &&
          (primaryConversationId !== userSessionRef.current.conversationId ||
            primaryConversationId !== userSessionRef.current.vcxConversationId)) ||
        (internalConversationId &&
          internalConversationId !== userSessionRef.current.internalConversationId);

      if (sessionNeedsUpdate) {
        console.log(
          "🔄 Syncing session with history ID:", primaryConversationId,
          "internal:", internalConversationId
        );

        const updatedSession = {
          ...userSessionRef.current,
          conversationId: primaryConversationId || userSessionRef.current.conversationId,
          vcxConversationId: primaryConversationId || userSessionRef.current.vcxConversationId,
          internalConversationId: internalConversationId || userSessionRef.current.internalConversationId,
        };

        if (!updatedSession.userId && userSessionRef.current.userId) {
          updatedSession.userId = userSessionRef.current.userId;
        }

        setUserSession(updatedSession);
        userSessionRef.current = updatedSession;
        sessionResetRef.current = false;

        // Re-subscribe to socket for the loaded conversation
        if (socketRef.current && updatedSession.userId) {
          socketRef.current.emit("subscribe", updatedSession.userId);
          console.log("🔄 Re-subscribed to socket for userId:", updatedSession.userId);
        }
      }

      // ── Step 7: format, sort, render ─────────────────────────────────────
      const finalFormattedMessages = uniqueRawMessages
        .filter((msg) => {
          if (msg.content?.type === 'text') {
            const text = msg.content?.data?.text || msg.content?.data?.body || '';
            if (text.startsWith('__EXEC_ID__:')) return false;
          }
          return true;
        })
        .map((msg) => processMessage(msg))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setMessages(finalFormattedMessages);

      console.log("✅ Loaded", finalFormattedMessages.length, "messages from API");

      return { success: true, messages: finalFormattedMessages };
    } catch (error) {
      console.error("❌ Error loading messages from history:", error);
      notifyError("Failed to load chat history");
      return { success: false, error: error.message };
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // ── POLL FOR POST PREVIEW ─────────────────────────────────────────────────
  // Called by Dashboard after firing the n8n webhook.
  // Uses the whatsapp/v2/chat/get-messages route (which accepts the CONVO_ ID)
  // as a fallback in case the socket delivery is missed during the generation window.
  const pollForPostPreview = useCallback((timeoutMs = 120000, intervalMs = 5000) => {
    const startTime = Date.now();

    const poll = async () => {
      if (Date.now() - startTime > timeoutMs) {
        console.warn("⏰ Post preview poll timed out");
        return;
      }

      const currentSession = userSessionRef.current;

      // We need the CONVO_ ID for this endpoint — pick the first CONVO_ value
      // from conversationId or vcxConversationId and never use a conv_ webchat ID.
      const convoId = [currentSession.conversationId, currentSession.vcxConversationId]
        .find(id => id && String(id).startsWith("CONVO_")) || "";
      const userId = currentSession.userId;

      if (!convoId || !userId) {
        console.warn("⚠️ pollForPostPreview: missing convoId or userId, retrying...");
        setTimeout(poll, intervalMs);
        return;
      }

      try {
        // Build the whatsapp/v2 URL — replace /webchat/v2 with /whatsapp/v2
        const baseUrl = CHAT_API.replace('/webchat/v2', '/whatsapp/v2');
        const url = `${baseUrl}/chat/get-messages/${convoId}/${userId}/${WEBCHAT_CONFIG.clientId}?page=1`;

        console.log("🔍 Polling for post preview:", url);

        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();

          // The response may be an array or { messages: [...] } or { data: [...] }
          const rawMessages = Array.isArray(data)
            ? data
            : (data.messages || data.data || data.message || []);

          // Check the most recent messages (last 10) for a post preview
          const recentMessages = Array.isArray(rawMessages)
            ? rawMessages.slice(-10)
            : [];

          for (const msg of recentMessages) {
            const text =
              msg.content?.data?.text ||
              msg.content?.data?.body ||
              (typeof msg.content === 'string' ? msg.content : '') ||
              '';

            const msgId = msg._id || msg.id || '';

            if (tryDispatchPostPreview(text, msgId)) {
              console.log('✅ Post preview found and dispatched via poll');
              return; // Stop polling — post preview delivered
            }
          }
        } else {
          console.warn("⚠️ Poll response not ok:", response.status);
        }
      } catch (err) {
        console.warn("⚠️ Poll error:", err.message);
      }

      // Schedule next poll
      setTimeout(poll, intervalMs);
    };

    // Start first poll after initial interval
    setTimeout(poll, intervalMs);
  }, [tryDispatchPostPreview]);
  // ── END POLL FOR POST PREVIEW ─────────────────────────────────────────────

  const stopResponse = useCallback(async () => {
    const execId = pendingExecutionIdRef.current;
    pendingExecutionIdRef.current = null;
    setCanStop(false);
    awaitingBotResponseRef.current = false;
    setIsTyping(false);
    setResponseDelayed(false);
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    if (!execId) {
      console.log('⚠️ stopResponse: no executionId to cancel');
      return;
    }
    console.log('🛑 Sending stop request for executionId:', execId);
    try {
      await fetch('https://workflow.igr-consulting.com/webhook/stop-execution', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'stop_requested',
          payload: { type: 'stop_requested', executionId: execId },
        }),
      });
      console.log('✅ Stop request sent via Botpress webhook');
    } catch (e) {
      console.warn('⚠️ Failed to send stop request:', e.message);
    }
  }, []);

  return {
    messages,
    isTyping,
    isConnected,
    isHistoryLoading,
    chatStatus,
    userSession,
    sendMessage,
    regenerateResponse,
    editAndResend,
    sendQuickReply,
    submitForm,
    resetChat,
    deleteBotpressMessages,
    loadMessages,
    loadMessagesFromHistory,
    config: WEBCHAT_CONFIG,
    handlePostback,
    insufficientCredits,
    planExpired,
    setPlanExpired,
    notification,
    responseDelayed,
    dismissResponseDelayed,
    registerPostPreviewHandler: (fn) => { onPostPreviewRef.current = fn; },
    pollForPostPreview,
    pendingExecutionIdRef,
    canStop,
    stopResponse,
  };
};
